import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, IconButton, Typography, Icon, TextField, InputAdornment, List, ListItem, Collapse, ListItemText, ListItemIcon } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import useStyles, { listStyles } from './ScoreTag.styles';
import cpUseStyles from '../../ChecklistScores/ChecklistScores.styles';
import { get, size } from 'global/lib/lodash';
import { Loader } from 'global/styles';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import gstyles from 'global/styles';
import getHighlightedText from 'global/components/highlight-text';
import useDebounce from 'global/hooks/useDebounce';
import { filterData } from '../utils';
import ScoreIcon from 'global/components/ScoreIcon';

const System = (props) => {
  const styles = listStyles();
  const [open, setOpen] = useState(get(props, 'alwaysOpen', false));

  const { system, search } = props;
  const title = get(system, 'name', '');
  const checklists = get(system, 'children', []);

  useEffect(() => {
    if (search) {
      setOpen(true);
    } else {
      setOpen(get(props, 'alwaysOpen', false));
    }
  }, [system]);

  return (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)}>
        <ListItemText primary={title} primaryTypographyProps={{ variant: 'button' }} />
        <ListItemIcon>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListItem>
      <Collapse in={open} unmountOnExit>
        <List disablePadding className={styles.marginLeft}>
          {checklists.map((check, i) => (
            <Checklist {...props} key={get(check, 'checklistId')} checklist={check} />
          ))}
        </List>
      </Collapse>
    </>
  );
};

const Checklist = (props) => {
  const styles = listStyles();
  const [open, setOpen] = useState(false);

  const { system, checklist, search } = props;
  const title = get(checklist, 'name', '');

  const checkpoints = get(checklist, 'children', []).filter((c) => c.checkpointId);

  useEffect(() => {
    const isMatch = get(props, 'tag.checklist.id') == get(checklist, 'checklistId');
    if (search || isMatch) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [checklist]);

  const tagMe = useCallback((checkpoint) => {
    props.changeTag({
      system,
      checklist,
      checkpoint,
    });
  });

  return (
    <>
      <ListItem className={styles.listItem} button onClick={() => setOpen(!open)}>
        <ListItemText primary={title} />
        <ListItemIcon className={styles.marginRight}>
          <Icon>{`arrow_drop_${open ? 'up' : 'down'}`}</Icon>
        </ListItemIcon>
      </ListItem>
      {!props.thinking && (
        <Collapse in={open} unmountOnExit>
          <List disablePadding>
            {checkpoints.map((check) => (
              <Checkpoint key={get(check, 'checkpointId')} checkpoint={check} onClick={tagMe} {...props} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

export const Checkpoint = (props) => {
  const { checkpoint, search } = props;

  const hasCriteria = get(checkpoint, 'criteria');
  const classes = cpUseStyles();

  const selectCheckpoint = () => {
    props.onClick({
      checkpoint,
    });
  };

  return (
    <ListItem button className={classes.topic} onClick={selectCheckpoint}>
      <div className={classes.rowTop}>
        <div className={classes.row}>
          <Typography variant="body2" className={classes.cpScoreHeaderChild}>
            {checkpoint.displayScore}
          </Typography>
          <ScoreIcon selected={false} center score={checkpoint.value} />
        </div>
        <Typography variant="body2">
          <strong>{getHighlightedText(checkpoint.name || '', search)}</strong>
          &nbsp;
          {getHighlightedText(hasCriteria || '', search)}
        </Typography>
      </div>
    </ListItem>
  );
};

export default (props) => {
  const { allScoreData, scorecard } = props;

  const [error, setError] = useState();
  const [tags, setTags] = useState({});
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 1000);
  const [thinking, setThinking] = useState(false);
  const [treed, setTreed] = useState();

  const runFilter = async (d = allScoreData) => {
    const { hasChildren } = await filterData(d, { search: debouncedSearch, value: 'any' });
    setTreed(hasChildren);
  };

  useEffect(() => {
    runFilter();
    setThinking(false);
  }, [debouncedSearch, allScoreData]);

  const classes = useStyles();
  const styles = gstyles();

  const handleClose = () => {
    props.onClose();
  };

  const reset = () => {
    setTags({});
    setSearch('');
    setThinking(false);
  };

  const changeTag = useCallback((tag) => {
    setTags(tag);
  });

  const searchChange = (val) => {
    setSearch(val);
    setThinking(true);
  };

  useEffect(() => {
    if (Object.keys(tags).length > 0) {
      props.onTagSelected({
        ...tags,
        // scorecardId,
      });
      props.onClose();
      reset();
    }
  }, [tags]);

  const showBlank = !size(treed); //size(textMatches.term) > 0 && !size(textMatches.systems);

  return (
    <Dialog className={classes.dialog} disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <div className={classes.container}>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" className={classes.subtitle}>
          Link To Criteria
        </Typography>

        <div className={classes.inputContainer}>
          <div className={classes.searchContainer}>
            <TextField
              variant="outlined"
              fullWidth
              id="search"
              value={search}
              label="Search"
              onChange={(e) => searchChange(e.target.value)}
              inputRef={(input) => input && input.focus()}
              InputProps={{
                endAdornment: (
                  <InputAdornment disablePointerEvents={!search} position="end">
                    <IconButton onClick={() => searchChange('')}>{thinking ? <Loader size={20} /> : <Icon>{search ? 'close' : 'search'}</Icon>}</IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className={clsx(classes.inputContainer, classes.list)}>
            {!!showBlank && (
              <div className={styles.centerColumn} style={{ padding: 20 }}>
                <span>{`There are no results that meet your search criteria.`}</span>
              </div>
            )}

            {!showBlank && (
              <List component="nav">
                {get(treed, '0.children', []).map((sys, i) => (
                  <System
                    key={i}
                    tag={props.tag}
                    changeTag={changeTag}
                    system={sys}
                    search={debouncedSearch}
                    thinking={thinking}
                    scorecardConfig={get(scorecard, 'config', {})}
                    alwaysOpen={size(get(treed, '0.children', [])) === 1 || sys.supersetId == get(props, 'tag.system.id')}
                  />
                ))}
              </List>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
